import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, RefreshCw, Send, X, Eye, Calendar } from "lucide-react";

const STATUS_COLORS = {
  pending: "text-amber-400",
  confirmed: "text-primary",
  cancelled: "text-muted-foreground",
  no_show: "text-destructive",
};

const PAY_COLORS = {
  none: "text-muted-foreground",
  pending: "text-amber-400",
  paid: "text-primary",
  failed: "text-destructive",
  refunded: "text-muted-foreground",
};

function formatDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("hr-HR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Zagreb" }).format(new Date(iso));
  } catch { return iso; }
}

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data } = await api.get("/admin/bookings", { params });
      setItems(data.items || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [status, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/admin/bookings/${id}`);
      setOpen(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const cancel = async (id) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status: "cancelled" });
      toast.success("Cancelled");
      setOpen(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resend = async (id) => {
    try {
      await api.post(`/admin/bookings/${id}/resend-confirmation`);
      toast.success("Confirmation re-sent");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-bookings-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Bookings</h1>
          </div>
          <Button variant="outline" onClick={load} className="border-border" data-testid="bookings-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr_1fr_auto] gap-3" data-testid="bookings-filters">
          <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="bookings-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="no_show">No-show</SelectItem>
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} placeholder="From" data-testid="bookings-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} placeholder="To" data-testid="bookings-to" />
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-border rounded-xl p-16 text-center text-muted-foreground">
            <Calendar className="w-8 h-8 mx-auto mb-3 opacity-60" /> No bookings yet.
          </div>
        ) : (
          <div className="border border-border rounded-xl overflow-x-auto" data-testid="bookings-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>When</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Party</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((b) => (
                  <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                    <TableCell className="font-mono text-xs">{b.code}</TableCell>
                    <TableCell className="text-xs">{formatDateTime(b.starts_at)}</TableCell>
                    <TableCell className="text-xs">
                      <div>{b.customer?.name}</div>
                      <div className="text-muted-foreground">{b.customer?.email}</div>
                    </TableCell>
                    <TableCell>{b.party_size}</TableCell>
                    <TableCell className={`${STATUS_COLORS[b.status] || ""} text-xs uppercase tracking-widest`}>{b.status}</TableCell>
                    <TableCell className={`${PAY_COLORS[b.payment_status] || ""} text-xs uppercase tracking-widest`}>{b.payment_status}</TableCell>
                    <TableCell className="text-xs">{Number(b.total_amount).toFixed(2)} €</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="border-border" onClick={() => openDetail(b.id)} data-testid={`booking-view-${b.id}`}>
                        <Eye className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <Sheet open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="booking-detail-sheet">
            {open && (
              <>
                <SheetHeader><SheetTitle>Booking {open.code}</SheetTitle></SheetHeader>
                <div className="space-y-4 text-sm mt-6">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-primary mb-1">Customer</div>
                    <div>{open.customer?.name}</div>
                    <div className="text-muted-foreground">{open.customer?.email}</div>
                    {open.customer?.phone && <div className="text-muted-foreground">{open.customer.phone}</div>}
                    {open.customer?.notes && <div className="mt-2 italic text-muted-foreground">“{open.customer.notes}”</div>}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-widest text-primary mb-1">When</div>
                      <div>{formatDateTime(open.starts_at)} → {formatDateTime(open.ends_at)}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-primary mb-1">Party</div>
                      <div>{open.party_size}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-primary mb-1">Tables</div>
                      <div>{(open.tables || []).map((t) => t.label).join(" + ") || "—"}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-primary mb-1">Status</div>
                      <div className={STATUS_COLORS[open.status]}>{open.status}</div>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-widest text-primary mb-1">Payment</div>
                      <div className={PAY_COLORS[open.payment_status]}>{open.payment_status} · {open.total_amount} {open.currency}</div>
                    </div>
                    {open.event_id && (
                      <div>
                        <div className="text-xs uppercase tracking-widest text-primary mb-1">Event ID</div>
                        <div className="font-mono text-xs">{open.event_id}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-3 border-t border-border">
                    <Button size="sm" variant="outline" className="border-border" onClick={() => resend(open.id)} data-testid="booking-detail-resend">
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Resend confirmation
                    </Button>
                    {open.status !== "cancelled" && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline" className="border-destructive/40 text-destructive" data-testid="booking-detail-cancel">
                            <X className="w-3.5 h-3.5 mr-1.5" /> Cancel booking
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Cancel booking {open.code}?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Keep</AlertDialogCancel>
                            <AlertDialogAction onClick={() => cancel(open.id)} className="bg-destructive text-destructive-foreground" data-testid="booking-cancel-confirm">Cancel booking</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AdminLayout>
  );
}
